/**
 * @coeldery/segmented-switch — **兩段式切換**（例：模式／檢視／排序）
 *
 * 為何係模組（R3）：任何 app 都會有「兩個狀態之間切換」嘅掣（而且一定要 a11y 正確、熱區夠大）
 *   → 重複手寫一定走樣。查庫證據（2026-09-22）：`segmented` 0 命中；`switch` 只中
 *   `locale-switcher`（語言專用：細掣＋選單，唔係兩段切換）→ 依 R3 新建。
 *
 * 血緣：cosingery（2026-09-22 產品負責人：「let the user click and select the mode …
 *   when click the left one, it is listener mode, and click on the right it become chat mode」；
 *   `decisions/027`）。
 *
 * ⚠️ 設計：模組**唔識**產品內容 —— `options`（id／label 由父層砌好）＋ `value` ＋ `onChange`。
 *   · `role="radiogroup"` ＋ 每個選項 `role="radio"`／`aria-checked`（唔用 tablist：唔係分頁）
 *   · 熱區 ≥44×44px；文字一律由父層傳（唔准寫死產品文案）
 *   · 選中／未選中只用 CSS var（唔准 hardcode 色）
 */
import { useTranslation } from 'react-i18next';
import { nextIndex } from './switch.ts';

export { nextIndex } from './switch.ts';

export interface SegmentedOption {
  /** 穩定 id（唔准用顯示文字做 key） */
  id: string;
  /** 顯示文字（已 i18n；由父層傳） */
  label: string;
}

export interface SegmentedSwitchProps {
  options: SegmentedOption[];
  value: string;
  onChange: (id: string) => void;
  /** 無障礙名稱（i18n key） */
  labelKey?: string;
  busy?: boolean;
  /** 額外樣式（父層可以夾落頂欄） */
  style?: React.CSSProperties;
}

export default function SegmentedSwitch({
  options,
  value,
  onChange,
  labelKey,
  busy = false,
  style,
}: SegmentedSwitchProps) {
  const { t } = useTranslation();
  const currentIndex = options.findIndex((o) => o.id === value);
  const active = currentIndex < 0 ? 0 : currentIndex;

  /** ← → 喺選項之間行（唔循環；`nextIndex` 係純函數，可單測） */
  const onKeyDown = (e: React.KeyboardEvent) => {
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (delta === 0) return;
    e.preventDefault();
    const next = nextIndex(options.length, active, delta);
    const target = options[next];
    if (target) onChange(target.id);
  };

  return (
    <div
      role="radiogroup"
      data-testid="segmented-switch"
      aria-label={labelKey ? t(labelKey) : undefined}
      onKeyDown={onKeyDown}
      style={{
        display: 'inline-flex',
        padding: '2px',
        gap: '2px',
        borderRadius: '999px',
        border: '1.5px solid var(--color-divider)',
        background: 'var(--color-card)',
        ...style,
      }}
    >
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            data-testid={`segment-${o.id}`}
            aria-checked={on}
            disabled={busy}
            onClick={() => onChange(o.id)}
            style={{
              minHeight: '32px',
              minWidth: '44px',
              padding: '0 12px',
              borderRadius: '999px',
              border: 'none',
              background: on ? 'var(--color-primary)' : 'transparent',
              color: on ? 'var(--color-on-primary)' : 'var(--color-text-secondary)',
              fontFamily: 'inherit',
              fontSize: 'var(--font-size-small, 15px)',
              fontWeight: 700,
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
